import type {
  MultiGroupedQuestion,
  SurveyAnswer,
  SurveyQuestion,
} from './types';

export const getInitialAnswers = (
  questions: SurveyQuestion[]
): SurveyAnswer[] =>
  questions.map((question) => {
    if (question.type === 'multi' || question.type === 'multi-grouped')
      return [];
    if (question.type === 'rating') return {};
    if (question.type === 'text') return '';
    return null;
  });

export const shouldShowQuestion = (
  questions: SurveyQuestion[],
  index: number,
  answers: SurveyAnswer[]
) => {
  const question = questions[index];
  if (!question || question.type !== 'text' || !question.conditional)
    return true;
  const answer = answers[question.conditional.dependsOn];
  return (
    typeof answer === 'number' &&
    question.conditional.showIfAnswerIndexes.includes(answer)
  );
};

export const getNextVisibleIndex = (
  questions: SurveyQuestion[],
  from: number,
  direction: 1 | -1,
  answers: SurveyAnswer[]
) => {
  let index = from + direction;
  while (
    index >= 0 &&
    index < questions.length &&
    !shouldShowQuestion(questions, index, answers)
  ) {
    index += direction;
  }
  return index;
};

const getGroupedOptionLabels = (
  question: MultiGroupedQuestion,
  selectedIndexes: number[]
) => {
  const labels: string[] = [];
  let index = 0;
  question.groups.forEach((group) => {
    group.options.forEach((option) => {
      if (selectedIndexes.includes(index)) labels.push(option);
      index += 1;
    });
  });
  return labels;
};

export const serializeAnswers = (
  questions: SurveyQuestion[],
  answers: SurveyAnswer[]
) =>
  questions.reduce<Record<string, unknown>>((acc, question, index) => {
    if (!shouldShowQuestion(questions, index, answers)) return acc;
    const answer = answers[index];
    const key = `q${index + 1}`;
    let answerValue: unknown;

    if (question.type === 'single') {
      answerValue =
        typeof answer === 'number' ? question.options[answer] : null;
    } else if (question.type === 'multi') {
      answerValue = Array.isArray(answer)
        ? answer.map((optionIndex) => question.options[optionIndex])
        : [];
    } else if (question.type === 'multi-grouped') {
      answerValue = Array.isArray(answer)
        ? getGroupedOptionLabels(question, answer)
        : [];
    } else if (question.type === 'rating') {
      const ratingAnswer =
        answer && typeof answer === 'object' && !Array.isArray(answer)
          ? answer
          : {};
      answerValue = question.rows.reduce<Record<string, string | null>>(
        (rowAcc, row, rowIndex) => {
          const scaleIndex = ratingAnswer[rowIndex];
          rowAcc[row] =
            typeof scaleIndex === 'number' ? question.scale[scaleIndex] : null;
          return rowAcc;
        },
        {}
      );
    } else {
      answerValue = answer;
    }

    acc[key] = { question: question.prompt, answer: answerValue };

    return acc;
  }, {});

export const canProceedFromQuestion = (
  question: SurveyQuestion,
  answer: SurveyAnswer
) => {
  if (question.type === 'text') return true;
  if (question.type === 'single') return answer !== null;
  if (question.type === 'multi' || question.type === 'multi-grouped') {
    return Array.isArray(answer) && answer.length > 0;
  }
  if (question.type === 'rating') {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer))
      return false;
    return question.rows.every((_, rowIndex) => answer[rowIndex] !== undefined);
  }
  return false;
};
